import { useCallback, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useFollowList } from './useFollowingPictures';

/**
 * Fetches the absolute freshest kind 3 follow list via the pool.
 * We re-fetch right before mutating to avoid overwriting stale data.
 */
async function fetchFreshFollowEvent(
  nostr: ReturnType<typeof useNostr>['nostr'],
  pubkey: string,
): Promise<NostrEvent | null> {
  const signal = AbortSignal.timeout(10_000);

  const followEvents = await nostr.query(
    [{ kinds: [3], authors: [pubkey], limit: 1 }],
    { signal },
  );

  if (followEvents.length === 0) return null;

  // Pick the most recent event across all relays
  return followEvents.reduce((latest, current) =>
    current.created_at > latest.created_at ? current : latest,
  );
}

export interface UseFollowActionsReturn {
  /** Whether a follow/unfollow mutation is in progress. */
  isPending: boolean;
  /** Follow a pubkey. Fetches the freshest kind 3 first, then publishes. */
  follow: (pubkey: string) => Promise<void>;
  /** Unfollow a pubkey. Fetches the freshest kind 3 first, then publishes. */
  unfollow: (pubkey: string) => Promise<void>;
}

/**
 * Safe follow / unfollow actions modelled after the ditto-5 follow-party approach.
 *
 * Key safety properties:
 * 1. Fetches the freshest kind 3 event from relays **right before** mutating.
 * 2. Picks the event with the highest `created_at` across all relay responses.
 * 3. Preserves **all** existing tags (not just `p` tags) so non-follow metadata is not lost.
 * 4. Preserves the `content` field (some clients store relay hints there).
 */
export function useFollowActions(): UseFollowActionsReturn {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  const [isPending, setIsPending] = useState(false);

  const mutateFollowList = useCallback(
    async (targetPubkey: string, action: 'follow' | 'unfollow') => {
      if (!user) throw new Error('Not logged in');
      setIsPending(true);

      try {
        // ① Fetch the freshest kind 3 event via pool
        const latestEvent = await fetchFreshFollowEvent(nostr, user.pubkey);

        // ② Separate tags into `p` tags (follow entries) and everything else
        const existingTags = latestEvent?.tags ?? [];
        const pTags = existingTags.filter(([name]) => name === 'p');
        const nonPTags = existingTags.filter(([name]) => name !== 'p');

        // ③ Compute the new set of `p` tags
        let newPTags: string[][];
        if (action === 'follow') {
          // Add only if not already present (dedup)
          const alreadyFollowed = pTags.some(([, pk]) => pk === targetPubkey);
          newPTags = alreadyFollowed ? pTags : [...pTags, ['p', targetPubkey]];
        } else {
          // Remove the target pubkey
          newPTags = pTags.filter(([, pk]) => pk !== targetPubkey);
        }

        // ④ Rebuild the full tag array: non-p tags first, then p tags
        const newTags = [...nonPTags, ...newPTags];

        // ⑤ Preserve the content field (relay hints / petnames in some clients)
        const content = latestEvent?.content ?? '';

        await publishEvent({
          kind: 3,
          content,
          tags: newTags,
        });

        // ⑥ Invalidate cached follow-list queries so UI updates
        queryClient.invalidateQueries({ queryKey: ['follow-list'] });
        // Also invalidate the following pictures feed so it refreshes
        queryClient.invalidateQueries({ queryKey: ['pictures', 'following'] });
      } finally {
        setIsPending(false);
      }
    },
    [nostr, user, publishEvent, queryClient],
  );

  const follow = useCallback(
    (pubkey: string) => mutateFollowList(pubkey, 'follow'),
    [mutateFollowList],
  );

  const unfollow = useCallback(
    (pubkey: string) => mutateFollowList(pubkey, 'unfollow'),
    [mutateFollowList],
  );

  return { isPending, follow, unfollow };
}

/**
 * Convenience hook — checks if a specific pubkey is in the logged-in user's follow list.
 */
export function useIsFollowing(pubkey: string): boolean {
  const { user } = useCurrentUser();
  const { data: followedPubkeys } = useFollowList(user?.pubkey);

  if (!followedPubkeys) return false;
  return followedPubkeys.includes(pubkey);
}
